import type { FunctionComponent } from 'react';
import { useEffect, useRef, useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useGeolocationIntegration } from '../../../geolocation/util/geolocation-context';
import { useLayout } from '../../../main/util/layout-context';
import { useDesktop } from '../../../shared/hooks/breakpoint';
import { useBalanceChange } from '../../../shared/hooks/use-balance-change';
import { useContextNavigate } from '../../../shared/hooks/use-context-navigate';
import { useSignIn } from '../../../shared/hooks/use-sign-in';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';
import type { SncBridgeImport } from '../../interface/sportnco-bridge.interface';
import { MessageMethod } from '../../interface/sportsnco-messaging.interface';

import styles from './sportnco.module.scss';

const style = createStyleHelper(styles, 'sportnco');

function notifySessionChange(
    sportsbook: SncBridgeImport,
    sessionId?: string,
    destination?: any,
    geolocationToken?: any,
) {
    if (!sportsbook || !sessionId) {
        return;
    }

    sportsbook.SncBridge.send(
        {
            method: MessageMethod.LOGIN,
            args: {
                sessionId,
                geolocationToken,
            },
        },
        [destination],
    );
}

export interface ISportNCoProps {
    className?: string;
    launchUrl: string;
    sessionId?: string;
}

export const SportNCo: FunctionComponent<ISportNCoProps> = ({
    className,
    launchUrl,
    sessionId,
}) => {
    useLayout({
        title: 'sports.title',
        showNavButtons: true,
        isFullWidth: true,
        isFixedHeight: true,
    });

    const { '*': sportsUrl } = useParams();
    const isDesktop = useDesktop();
    const sportsNavigationRef = useRef<HTMLDivElement>(null);
    const loaderRef = useRef<HTMLDivElement>(null);
    const frameRef = useRef<HTMLIFrameElement>(null);
    const sportsbook = useRef<SncBridgeImport | null>(null);
    const navigate = useContextNavigate();
    const signIn = useSignIn();
    const integration = useGeolocationIntegration();
    const geolocation = integration?.getCurrentToken();
    const [iframeHeight, setIframeHeight] = useState('1200');

    useEffect(() => {
        let cancelled = false;
        async function initialize() {
            if (!frameRef.current) {
                return;
            }

            const bridge = (await import(
                /* @vite-ignore */ `${launchUrl}lib/snc-bridge.js`
            )) as SncBridgeImport;

            sportsbook.current = bridge;

            if (cancelled) {
                return;
            }

            const { SncBridge } = bridge;
            SncBridge.init(frameRef.current, () => {
                if (cancelled || !loaderRef.current || !sportsNavigationRef.current) {
                    return;
                }

                // Subscribe to the login event
                SncBridge.subscribe({
                    name: 'request_login_open',
                    condition: (event) => event?.data?.event === 'request_login_open',
                    callback: () => {
                        signIn();
                    },
                });

                //Synchronize the URL address bar with the sportsbook
                SncBridge.subscribe({
                    name: 'state-change',
                    condition: (event) => event?.data.url,
                    callback: (event) => {
                        navigate(`/sports${event.data.url}`);
                    },
                });
                // Inject the loader and tab bar into our component
                SncBridge.requestContent({
                    contentId: 'loading-container',
                    destinationId: loaderRef.current,
                });
                SncBridge.requestContent({
                    contentId: 'tab-bar',
                    destinationId: sportsNavigationRef.current,
                });

                if (!isDesktop) {
                    SncBridge.send({
                        method: MessageMethod.OPENING,
                    });
                }

                notifySessionChange(bridge, sessionId, frameRef?.current, geolocation);
                //notifyGeolocationChange(bridge, frameRef?.current, geolocation);

                SncBridge.subscribe({
                    name: 'sportbook_request_external_scroll',
                    condition: (event) =>
                        event?.data?.event === 'sportbook_request_external_scroll',
                    callback: () => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    },
                });
                // height changing functionality
                SncBridge.subscribe({
                    name: 'sportnco_window_resize',
                    condition: (event) => event?.data.event === 'sportnco_window_resize',
                    callback: (event) => {
                        setIframeHeight(event?.data?.height);
                    },
                });
            });
        }

        initialize();

        return () => {
            // Destroy? Why isn't there a destroy method?
            cancelled = false;
        };
    }, [launchUrl, isDesktop, navigate, sessionId, signIn, geolocation]);

    //Geotoken
    useEffect(() => {
        if (!sportsbook || !frameRef.current || !geolocation || !sessionId) {
            return;
        }

        sportsbook.current?.SncBridge.send(
            {
                method: MessageMethod.LOGIN,
                args: {
                    sessionId,
                    geolocationToken: geolocation,
                },
            },
            [frameRef.current],
        );
    }, [frameRef, geolocation, sportsbook, sessionId]);

    useEffect(() => {
        if (!sportsbook || !frameRef.current || sessionId) {
            return;
        }

        sportsbook.current?.SncBridge.send(
            {
                method: MessageMethod.LOGOUT,
                args: {
                    sessionId: sessionId,
                },
            },
            [frameRef.current],
        );
    }, [frameRef, sessionId]);

    useEffect(() => {
        if (!sportsbook || !frameRef.current) {
            return;
        }

        sportsbook.current?.SncBridge.send(
            {
                method: MessageMethod.READY,
                message: 'external_tokens_iframe_ready',
            },
            [frameRef.current],
        );
    }, [frameRef, sportsbook]);

    useBalanceChange(
        useCallback(() => {
            if (!frameRef.current || !sportsbook.current) {
                return;
            }

            sportsbook.current.SncBridge?.send(
                {
                    method: MessageMethod.RELOAD_BALANCE,
                },
                [frameRef?.current],
            );
        }, [frameRef]),
    );

    // Synchronize URL only once to avoid reloading the iframe
    const fullSportsbookSrc = useRef(`${launchUrl}${sportsUrl}`);

    return (
        <>
            <div ref={loaderRef}></div>
            <div className={createClassName([className, style()])}>
                <iframe
                    className={style('frame')}
                    ref={frameRef}
                    height={iframeHeight}
                    src={fullSportsbookSrc.current}
                />
                {/* SportNCo require the div to be completely empty - including no class names */}
                <div className={style('navigation')}>
                    <div ref={sportsNavigationRef}></div>
                </div>
            </div>
        </>
    );
};
